import React, { useState, useRef, useEffect } from "react";
import "./clientInvoicesDisplay.css";
import BdrDisclosure from "../../../../components/BdrDisclosure/BdrDisclosure";
import { priceStrFormatter } from "../../../../utils/formatters/priceStrFormatter";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import { useMediaQuery } from "@mui/material";
import BAGroupTimeFormat from "../../../../utils/formatters/BAGroupTimeFormat";
import InsertLinkIcon from "@mui/icons-material/InsertLink";
import copyStringToClipboard, { copyAsyncString } from "../../../../utils/formatters/copyStringToClipboard";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import EditIcon from "@mui/icons-material/Edit";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import { useSnackbar } from "../../../../utils/GlobalSnackbar/GlobalSnackbarContext";
import { useMutation } from "@apollo/client";
import { GET_PUBLIC_SHARE_URL } from "../../../../utils/GraphQL/mutations";
import CycleConfirmationModal from "../CycleConfirmationModal/CycleConfirmationModal";

/*
 * Two hosts, one component.
 *
 * `inPanel` renders this straight into the client console's VS panel: no
 * BdrDisclosure wrapper (the panel header already names the section, and only
 * one section is on screen at a time), always open, and none of the
 * document-scroll behaviour — the console's page is viewport-locked and the
 * PANEL scrolls, so `window.scrollTo` there moves the wrong thing.
 *
 * Without it, this is the collapsible row the Edit branch still uses,
 * unchanged. `barCollapsed` is a PARENT-owned prop, which is why panel mode
 * ignores it rather than trying to force it open from in here.
 */
const ClientInvoicesDisplay = ({
  heightTrigger,
  invoicesArr,
  openInvoiceModal,
  barCollapsed,
  setBarCollapsed,
  openEditInvoiceQuoteModal,
  inPanel,
}) => {
  const { handleSnack } = useSnackbar();
  const [collapseTrigger, setCollapseTrigger] = useState(null);
  const smaller900 = useMediaQuery("(max-width:900px)");
  const clientInvoicesDiv = useRef();
  const [getPublicShareUrl] = useMutation(GET_PUBLIC_SHARE_URL);
  const [cycleTaskId, setCycleTaskId] = useState(null);

  // Money still owed is the one thing worth reporting on the collapsed bar.
  const unpaidInvoices =
    invoicesArr?.filter((invoice) => !invoice?.invoicePaid)?.length || 0;

  /*
   * NEWEST FIRST. The list arrived in whatever order the client document held
   * them, which read as random: Mar 2026, Apr 2025, Feb 2023, May 2024. Sorted
   * on a COPY — `invoicesArr` is the parent's array and sorting in place
   * mutates the client object other sections read from.
   *
   * `jobScheduledFor` before `dateCreated` for the same reason the rail's
   * "Since" line uses it: on an imported job the scheduled date is the work,
   * while dateCreated can be the date of the import run. Number(), not a
   * string compare - these are epoch-ms strings and a lexicographic sort goes
   * wrong the moment digit count changes.
   */
  const sortedInvoices = [...(invoicesArr || [])].sort((a, b) => {
    const at = Number(a?.jobScheduledFor || a?.dateCreated) || 0;
    const bt = Number(b?.jobScheduledFor || b?.dateCreated) || 0;
    return bt - at;
  });

  useEffect(() => {
    if (collapseTrigger !== null) {
      if (heightTrigger) {
        heightTrigger();
      }

      // The console's page does not scroll; its panel does.
      if (inPanel) return undefined;

      const id = setTimeout(() => {
        if (smaller900 === true && barCollapsed === true) {
          const navHeight = document.querySelector(
            "#admin-nav-height-marker"
          )?.offsetHeight;
          const pagePos = clientInvoicesDiv.current.offsetTop;

          const totalMobileScroll = pagePos - navHeight - 5;
          window.scrollTo(0, totalMobileScroll);
        }

        // if (smaller900 === false && barCollapsed === true) {
        //   clientInvoicesDiv.current.scrollIntoView({ block: "start" });
        // }
      }, 500);

      setBarCollapsed(collapseTrigger);

      return () => clearTimeout(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collapseTrigger]);

  const openBarCollapse = () => {
    setCollapseTrigger((prev) => {
      if (prev === null) {
        return false;
      }

      return !prev;
    });
  };

  const listBody = (
    invoicesArr?.length > 0 ? (
          <div className="client-invoices-info-display">
            <ul>
              {sortedInvoices?.map((invoice, index) => {
                /* Cents STRING to a display string - never parseFloat on a
                   money value (CLAUDE.md). */
                const total = priceStrFormatter(invoice?.invoiceTotal || "0");
                return (
                  <li key={`invoice-item-${invoice?.id}-${index}`}>
                    <div
                      className="client-invoices-item-box bdr-border-thin"
                      onClick={() => {
                        openInvoiceModal(invoice?.id);
                      }}
                    >
                      <div className="invoice-item-info-box">
                        {invoice?.invoicePaid ? (
                          <div className="client-invoices-paid-icon-box">
                            {/* The stamp said "Paid" and nothing more. The
                                business is paid on the day of the job (owner),
                                so jobScheduledFor IS the payment date in all
                                but a rare exception - the row can say WHEN. */}
                            <span
                              className={`client-invoices-paid-icon`}
                              title={`Paid ${BAGroupTimeFormat(
                                invoice?.jobScheduledFor,
                                false,
                                true,
                                true
                              )}`}
                            >
                              Paid
                            </span>
                          </div>
                        ) : (
                          <div className="client-invoices-paid-icon-box">
                            <span className={`client-invoices-pending-icon`}>
                              Pending...
                            </span>
                          </div>
                        )}
                        {/* <span className={`client-invoices-circle`} /> */}
                        <span className="invoice-item-label">
                          <span className="invoice-item-num">
                            {invoice?.invoiceNum}
                          </span>
                          <span className="invoice-item-date">
                            {invoice?.invoicePaid ? "Paid " : ""}
                            {BAGroupTimeFormat(
                              invoice?.jobScheduledFor,
                              false,
                              true,
                              true
                            )}
                          </span>
                        </span>
                        {/* The amount was the one thing this row never said.
                            Pushed to the middle, which was dead space. */}
                        <span
                          className={`invoice-item-total${
                            invoice?.invoicePaid ? "" : " is-owed"
                          }`}
                        >
                          {total}
                        </span>
                      </div>
                      <div className="invoice-item-actions-box">
                        {navigator.clipboard && (
                          <button
                            className="client-invoices-quotes-copy-btn "
                            onClick={async (event) => {
                              event.stopPropagation();
                              event.preventDefault();
                              const host = window.location.host;
                              const domain = /localhost/gi.test(host)
                                ? host
                                : "https://bigginsdoorrefinishing.com";
                              const fallback = `${domain}/client/view-invoice/${invoice?.id}`;
                              const urlPromise = getPublicShareUrl({
                                variables: { docType: "invoice", docId: invoice?.id },
                              })
                                .then(({ data }) => data?.getPublicShareUrl || fallback)
                                .catch(() => fallback);
                              const ok = await copyAsyncString(urlPromise, fallback);
                              handleSnack(
                                ok
                                  ? "Invoice Link Copied"
                                  : "Failed to copy invoice link — try again",
                                ok ? 2500 : 4000
                              );
                            }}
                          >
                            <InsertLinkIcon
                              sx={{ fontSize: "18px" }}
                              titleAccess="Copy Invoice Link"
                            />
                          </button>
                        )}
                        <button
                          className="client-invoices-quotes-copy-btn"
                          onClick={async (event) => {
                            event.stopPropagation();
                            event.preventDefault();
                            openEditInvoiceQuoteModal(true, invoice?.id);
                          }}
                        >
                          <EditIcon
                            sx={{ fontSize: "18px" }}
                            titleAccess="Edit Invoice"
                          />
                        </button>
                        {navigator.clipboard && (
                          <button
                            className="client-invoices-quotes-copy-btn"
                            onClick={async (event) => {
                              event.stopPropagation();
                              event.preventDefault();
                              const host = window.location.host;
                              const domain = /localhost/gi.test(host)
                                ? host
                                : "https://bigginsdoorrefinishing.com";
                              const address = `${domain}/login-portal/payment/${invoice?.id}`;
                              const ok = await copyStringToClipboard(address);
                              handleSnack(
                                ok
                                  ? "Payment Link Copied"
                                  : "Failed to copy payment link — try again",
                                ok ? 2500 : 4000
                              );
                            }}
                          >
                            <AttachMoneyIcon
                              sx={{ fontSize: "18px" }}
                              titleAccess="Copy Payment Link"
                            />
                          </button>
                        )}
                        {invoice?.relatedTaskId && (
                          <button
                            className="client-invoices-quotes-copy-btn"
                            onClick={(event) => {
                              event.stopPropagation();
                              event.preventDefault();
                              setCycleTaskId(invoice.relatedTaskId);
                            }}
                          >
                            <AutorenewIcon
                              sx={{ fontSize: "18px" }}
                              titleAccess="Maintenance Cycle Position"
                            />
                          </button>
                        )}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
    ) : (
      <div className="scc-list-empty">No invoices to display</div>
    )
  );

  const cycleModal = (
    <CycleConfirmationModal
      open={!!cycleTaskId}
      taskId={cycleTaskId}
      onClose={() => setCycleTaskId(null)}
    />
  );

  if (inPanel) {
    return (
      <div className="scc-list" ref={clientInvoicesDiv}>
        {listBody}
        {cycleModal}
      </div>
    );
  }

  return (
    <div
      id="client-invoices-height-marker"
      className="client-invoices-container"
      ref={clientInvoicesDiv}
    >
      <BdrDisclosure
        open={!barCollapsed}
        onToggle={openBarCollapse}
        label="Invoices"
        icon={<ReceiptLongIcon />}
        count={invoicesArr?.length}
        status={
          unpaidInvoices > 0
            ? { tone: "warning", text: `${unpaidInvoices} unpaid` }
            : undefined
        }
        emptyText="No invoices yet"
        bodyScroll="cap"
      >
        {listBody}
      </BdrDisclosure>
      {cycleModal}
    </div>
  );
};

export default ClientInvoicesDisplay;
